import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useInHouse, useMyShift, useOpenOrders, useRsMutation, useTables, type DiningTable, type Order } from '../api/restaurant';
import { formatGhs } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport } from '../ui/kit';
import { PageHead } from './shared';

export function Restaurant() {
  const { can } = useAuth();
  const tables = useTables();
  const orders = useOpenOrders();
  const shift = useMyShift(can('shift.manage'));
  const nav = useNavigate();
  const report = useReport();
  const [pick, setPick] = useState<DiningTable | null>(null);
  const [covers, setCovers] = useState('2');
  const [takeaway, setTakeaway] = useState(false);
  const [room, setRoom] = useState(false);

  const open = useRsMutation((body: Record<string, unknown>) => api<Order>('/orders', { method: 'POST', body }));
  const start = (body: Record<string, unknown>) =>
    open.mutate(body, {
      onSuccess: (o) => nav(`/restaurant/orders/${(o as Order).id}`),
      onError: report,
    });

  const others = (orders.data ?? []).filter((o) => o.target !== 'TABLE');
  const byArea = new Map<string, DiningTable[]>();
  for (const t of tables.data ?? []) byArea.set(t.area ?? 'Tables', [...(byArea.get(t.area ?? 'Tables') ?? []), t]);

  return (
    <div className="page">
      <PageHead eyebrow="Restaurant and bar" title="Floor">
        <Button onClick={() => setTakeaway(true)}>Takeaway</Button>
        <Button onClick={() => setRoom(true)}>Charge to a room</Button>
      </PageHead>

      {can('shift.manage') ? (
        <div className={`banner${shift.data ? ' good' : ''}`}>
          {shift.isLoading ? 'Checking your till…' : shift.data ? <>Your till session is open. <Link to="/till">View or close it</Link></> : <>Your till is closed, so you cannot take cash, MoMo or card yet. <Link to="/till">Open it</Link></>}
        </div>
      ) : null}

      {tables.isLoading ? (
        <Loading rows={3} />
      ) : tables.error ? (
        <ErrorNote error={tables.error} retry={() => void tables.refetch()} />
      ) : (tables.data ?? []).length === 0 ? (
        <div className="card flat">
          <Empty icon="rooms" title="No tables set up yet">
            {can('table.manage') ? <Link to="/menu">Add your tables under Menu and tables.</Link> : 'Ask a manager to add the tables.'}
          </Empty>
        </div>
      ) : (
        [...byArea.entries()].map(([area, list]) => (
          <section key={area} className="stack">
            <div className="eyebrow">{area}</div>
            <div className="rooms">
              {list.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`room ${t.openOrder ? 's-OCCUPIED' : 's-VACANT_CLEAN'}`}
                  style={{ textAlign: 'left', cursor: 'pointer' }}
                  onClick={() => (t.openOrder ? nav(`/restaurant/orders/${t.openOrder.id}`) : (setCovers('2'), setPick(t)))}
                >
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span className="no num">{t.name}</span>
                    <Badge tone={t.openOrder ? 'warn' : 'good'}>{t.openOrder ? 'Open' : 'Free'}</Badge>
                  </div>
                  <span className="small muted">{t.openOrder ? `${t.openOrder.covers} guest${t.openOrder.covers === 1 ? '' : 's'}` : `Seats ${t.capacity}`}</span>
                  {t.openOrder ? <span className="num">{formatGhs(t.openOrder.total)}</span> : null}
                </button>
              ))}
            </div>
          </section>
        ))
      )}

      {others.length > 0 ? (
        <section className="stack">
          <div className="eyebrow">Takeaway and room orders</div>
          <div className="list">
            {others.map((o) => (
              <Link key={o.id} to={`/restaurant/orders/${o.id}`} className="item">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="title">{o.label ?? (o.target === 'TAKEAWAY' ? 'Takeaway' : 'Room order')}</span>
                  <span className="num">{formatGhs(o.total)}</span>
                </div>
                <span className="small muted">
                  {o.reference} · {o.target === 'ROOM_STAY' ? 'Room charge' : 'Takeaway'} · {o.waiter}
                </span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <Modal open={!!pick} onClose={() => setPick(null)} title={pick ? `Table ${pick.name}` : 'Table'}>
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (pick) start({ target: 'TABLE', tableId: pick.id, covers: Math.max(1, Number(covers) || 1) });
          }}
        >
          <Field id="cv" label="How many guests?">
            <input id="cv" className="input" inputMode="numeric" value={covers} onChange={(e) => setCovers(e.target.value.replace(/\D/g, ''))} autoFocus />
          </Field>
          <div className="modal-foot">
            <Button type="button" onClick={() => setPick(null)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={open.isPending}>
              Start order
            </Button>
          </div>
        </form>
      </Modal>

      <TakeawayDialog open={takeaway} onClose={() => setTakeaway(false)} onStart={(label) => start({ target: 'TAKEAWAY', ...(label ? { label } : {}) })} pending={open.isPending} />
      <RoomDialog open={room} onClose={() => setRoom(false)} onPick={(bookingId) => start({ target: 'ROOM_STAY', bookingId })} pending={open.isPending} />
    </div>
  );
}

function TakeawayDialog({ open, onClose, onStart, pending }: { open: boolean; onClose: () => void; onStart: (label: string) => void; pending: boolean }) {
  const [label, setLabel] = useState('');
  return (
    <Modal open={open} onClose={onClose} title="Takeaway order">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          onStart(label.trim());
        }}
      >
        <Field id="tl" label="Name or note (optional)" hint="So the order can be found when it is ready">
          <input id="tl" className="input" value={label} onChange={(e) => setLabel(e.target.value)} autoFocus />
        </Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={pending}>
            Start order
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function RoomDialog({ open, onClose, onPick, pending }: { open: boolean; onClose: () => void; onPick: (bookingId: string) => void; pending: boolean }) {
  const [search, setSearch] = useState('');
  const guests = useInHouse(search.trim(), open);
  return (
    <Modal open={open} onClose={onClose} title="Charge to a room">
      <div className="stack">
        <Field id="rg" label="Room number or guest name">
          <input id="rg" className="input" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus autoComplete="off" />
        </Field>
        {guests.isLoading ? (
          <Loading rows={2} />
        ) : (guests.data ?? []).length === 0 ? (
          <p className="small muted">No checked-in guest matches. Only guests who are in the hotel can charge to a room.</p>
        ) : (
          <div className="list">
            {guests.data!.map((g) => (
              <button key={g.bookingId} type="button" className="item" style={{ textAlign: 'left', cursor: 'pointer' }} disabled={pending} onClick={() => onPick(g.bookingId)}>
                <span className="title">
                  Room {g.room ?? '?'} · {g.guest}
                </span>
                <span className="small muted num">{g.reference}</span>
              </button>
            ))}
          </div>
        )}
        <Button block onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
